import { useState, useEffect } from 'react';
import { useInventorProfile } from '../../hooks/useInventorProfile';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { validateImageFile } from '../../lib/uploadValidation';

export default function InventorProfileForm() {
  const { user } = useAuth();
  const { profile, loading, saving, save } = useInventorProfile();
  const [form, setForm] = useState({
    full_name: '', bio: '', portfolio_url: '', avatar_url: '',
  });
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        bio: profile.bio || '',
        portfolio_url: profile.portfolio_url || '',
        avatar_url: profile.avatar_url || '',
      });
    }
  }, [profile]);

  const update = (key, val) => { setForm(prev => ({ ...prev, [key]: val })); setSaved(false); };

  const uploadAvatar = async (file) => {
    const err = validateImageFile(file);
    if (err) { alert(err); return; }
    setUploading(true);
    const ext = file.name.split('.').pop();
    const path = `inventor-avatars/${user.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('event-assets').upload(path, file);
    if (error) { alert('Upload failed: ' + error.message); setUploading(false); return; }
    const { data: urlData } = supabase.storage.from('event-assets').getPublicUrl(path);
    update('avatar_url', urlData.publicUrl);
    setUploading(false);
  };

  const handleSave = async () => {
    try {
      await save(form);
      setSaved(true);
    } catch (err) {
      alert('Save failed: ' + err.message);
    }
  };

  if (loading) return <div className="text-sm text-gray-400">Loading profile...</div>;

  const inputCls = "w-full px-4 py-3 rounded-input border-2 border-vault-border text-sm font-body text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none transition-colors";
  const labelCls = "block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5";

  return (
    <div className="glass p-7 max-w-2xl">
      <h2 className="font-heading text-xl text-vault-navy mb-5">Inventor Profile</h2>

      <div className="space-y-5">
        <div>
          <label className={labelCls}>Full Name *</label>
          <input className={inputCls} value={form.full_name} onChange={e => update('full_name', e.target.value)} />
        </div>

        <div>
          <label className={labelCls}>Bio</label>
          <textarea
            className={inputCls + " min-h-[100px]"}
            value={form.bio}
            onChange={e => update('bio', e.target.value)}
            placeholder="Tell companies about yourself and your inventions..."
            rows={4}
          />
        </div>

        <div>
          <label className={labelCls}>Portfolio URL</label>
          <input className={inputCls} value={form.portfolio_url} onChange={e => update('portfolio_url', e.target.value)} placeholder="https://..." />
        </div>

        <div>
          <label className={labelCls}>Profile Photo</label>
          {form.avatar_url && <img src={form.avatar_url} alt="Avatar" className="w-16 h-16 rounded-full object-cover mb-2" />}
          <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadAvatar(e.target.files[0])} disabled={uploading} />
        </div>

        <div className="flex items-center gap-4 pt-4 border-t border-vault-border">
          <button
            onClick={handleSave}
            disabled={saving || !form.full_name}
            className="px-6 py-2.5 rounded-btn text-sm font-semibold text-white shadow-btn disabled:opacity-50 transition-all"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          {saved && <span className="text-emerald-600 text-sm font-medium">Saved!</span>}
        </div>
      </div>
    </div>
  );
}
